import {
    useState,
    type FC,
    useRef,
    type CSSProperties,
    type ReactNode,
    forwardRef,
    type ForwardRefRenderFunction,
} from 'react';
import classnames from 'classnames';
import LazyImage from 'dibs-react-lazy-image';
import { ioParams } from 'dibs-fastly/exports/imageOptimizations';
import { useIntl } from 'dibs-react-intl';
import { type Locale } from 'dibs-intl/exports/locales';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';

import styles from './styles/HeroBannerLazyImage.scss';

const imgParams = ioParams({
    'resize-filter': 'bilinear',
    format: 'pjpg',
    auto: 'webp',
    optimize: 'low',
}).toQuery();

const MaybeLinkComponent: ForwardRefRenderFunction<
    HTMLAnchorElement & HTMLDivElement,
    {
        style?: CSSProperties;
        href?: string | null;
        className?: string;
        tabIndex?: number;
        children?: ReactNode;
        onClick?: () => void;
        locale: Locale;
    }
> = ({ href, children, locale, ...rest }, ref) =>
    href ? (
        /**
         * Cant use Link because of ref types.
         * See https://github.com/1stdibs/ferrum/pull/12945#discussion_r712225737
         */
        // eslint-disable-next-line react/forbid-elements
        <a href={handleLocaleUrl(href, locale)} ref={ref} {...rest}>
            {children}
        </a>
    ) : (
        <div ref={ref} {...rest}>
            {children}
        </div>
    );
const MaybeLink = forwardRef(MaybeLinkComponent);

const convertStringToNumber = (value: number | string | null): number => {
    return parseInt(value?.toString() || '', 10);
};

type Props = {
    className?: string;
    imageHeight?: number | string | null;
    imageWidth?: number | string | null;
    ariaHidden?: boolean;
    href?: string | null;
    src: string;
    alt?: string;
    ariaDescribedBy?: string;
    onClick?: () => void;
};

const HpLazyImageWrapper: FC<Props> = ({
    imageHeight = 0,
    imageWidth = 0,
    ariaHidden,
    className,
    src,
    href,
    alt,
    ariaDescribedBy,
    onClick,
}) => {
    const wrapperRef = useRef<HTMLAnchorElement & HTMLDivElement>(null);
    const [showShimmer, setShowShimmer] = useState(true);

    const aspectRatio = convertStringToNumber(imageHeight) / convertStringToNumber(imageWidth);
    const img = (
        <img
            className={styles.image}
            src={src + imgParams}
            aria-describedby={ariaDescribedBy}
            aria-hidden={ariaHidden ? true : undefined}
            alt={alt}
        />
    );
    const intl = useIntl();
    return (
        <MaybeLink
            href={href}
            ref={wrapperRef}
            onClick={onClick}
            tabIndex={ariaHidden ? -1 : undefined}
            className={classnames(styles.container, className)}
            locale={intl.locale}
            style={{ paddingTop: aspectRatio ? aspectRatio * 100 + '%' : 'unset' }}
        >
            <LazyImage
                visibilityRef={wrapperRef}
                offsetVertical={300}
                offsetHorizontal={800}
                placeholderClass={classnames(
                    styles.lazyPlaceholder,
                    showShimmer ? styles.shimmer : ''
                )}
                className={className}
                onImageLoaded={() => {
                    setShowShimmer(false);
                }}
                placeholder={' '}
            >
                {img}
            </LazyImage>
            <noscript>{img}</noscript>
        </MaybeLink>
    );
};

export default HpLazyImageWrapper;
